/**
 * adminApiClient — Admin API 단일 인증 HTTP 클라이언트
 *
 * 모든 /api/admin/* 요청이 이 클라이언트를 통과한다.
 * hooks/pages는 token 구현 세부사항을 몰라야 한다.
 *
 * 책임:
 * - Firebase currentUser.getIdToken() 요청 직전 호출
 * - Authorization: Bearer ... 자동 첨부
 * - requestId 생성 및 전달
 * - 10초 timeout (backend interactive analytics budget보다 길게 유지)
 * - AbortSignal 전달
 * - 401 invalid_token → getIdToken(true) 1회 force refresh → 1회 재시도
 * - 동시 다수 401 → single-flight refresh (모두 같은 promise 공유)
 * - 2번째 401 / 403 / missing_token → 즉시 throw, retry 없음
 * - standardized AdminApiError
 *
 * 금지:
 * - token string을 module-level 또는 state에 캐시하지 않는다
 * - 무한 retry
 * - token을 URL/query parameter로 전달
 */

import { adminAuth } from '../../../../packages/firebase/adminClient'

export const ADMIN_API_TIMEOUT_MS = 10_000

function generateRequestId() {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  }
}

export class AdminApiError extends Error {
  constructor({ status, code, requestId, message }) {
    super(message || code || `HTTP ${status}`)
    this.name = 'AdminApiError'
    this.status = status
    this.code = code
    this.requestId = requestId
  }
}

/**
 * Single-flight token refresh state.
 * 동시에 여러 요청이 401을 받아도 force refresh는 정확히 1회만 실행된다.
 * logout/user 교체 시 refreshGeneration을 증가시켜 stale refresh 결과를 무효화한다.
 */
let pendingRefresh = null
let refreshGeneration = 0

/** 외부에서 user 교체/logout 시 호출 — stale refresh 결과가 섞이지 않도록 */
export function invalidateAdminApiClientRefresh() {
  refreshGeneration += 1
  pendingRefresh = null
}

/**
 * createAdminApiClient(getFirebaseUser)
 *
 * @param {() => import('firebase/auth').User | null} getFirebaseUser
 *   Firebase currentUser를 반환하는 함수 (AdminAuthProvider의 stable ref)
 * @returns {{ get, post }}
 */
export function createAdminApiClient(getFirebaseUser) {
  async function acquireToken(forceRefresh = false) {
    const user = getFirebaseUser()
    if (!user) throw new AdminApiError({ status: 401, code: 'missing_token', requestId: null, message: 'No authenticated user' })
    const token = await user.getIdToken(forceRefresh)
    // Firebase auth can change while a token request is pending. Never send a
    // token obtained for the preceding session.
    if (getFirebaseUser() !== user) {
      throw new AdminApiError({ status: 401, code: 'missing_token', requestId: null, message: 'Authentication session changed' })
    }
    return token
  }

  async function singleFlightRefresh() {
    const generation = refreshGeneration
    const user = getFirebaseUser()
    if (!user) throw new AdminApiError({ status: 401, code: 'missing_token', requestId: null, message: 'No authenticated user' })
    if (pendingRefresh?.user === user && pendingRefresh.generation === generation) return pendingRefresh.promise
    const promise = acquireToken(true).finally(() => {
      if (pendingRefresh?.promise === promise) pendingRefresh = null
    })
    pendingRefresh = { user, generation, promise }
    return promise
  }

  async function request(method, path, { params, body, signal } = {}) {
    const requestId = generateRequestId()
    const query = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value != null) query.set(key, String(value))
      })
    }
    const url = query.size ? `${path}${path.includes('?') ? '&' : '?'}${query}` : path

    async function attempt(forceRefresh) {
      let token
      if (forceRefresh) {
        token = await singleFlightRefresh()
      } else {
        token = await acquireToken(false)
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), ADMIN_API_TIMEOUT_MS)
      const combinedSignal = signal
        ? AbortSignal.any
          ? AbortSignal.any([signal, controller.signal])
          : controller.signal
        : controller.signal

      let response
      try {
        response = await fetch(url, {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Request-ID': requestId,
          },
          ...(body != null ? { body: JSON.stringify(body) } : {}),
          signal: combinedSignal,
        })
      } finally {
        clearTimeout(timeoutId)
      }

      const json = await response.json().catch(() => ({}))
      const serverCode = json?.code || null
      const serverRequestId = json?.requestId || requestId

      if (!response.ok) {
        throw new AdminApiError({
          status: response.status,
          code: serverCode || `http_${response.status}`,
          requestId: serverRequestId,
        })
      }

      return { data: json, requestId: serverRequestId }
    }

    // First attempt with cached token
    let firstError
    try {
      return await attempt(false)
    } catch (error) {
      if (!(error instanceof AdminApiError)) throw error
      firstError = error
    }

    // 401 처리
    if (firstError.status === 401 && firstError.code === 'invalid_token') {
      // Only the server's explicit invalid-token contract is refreshable.
      try {
        return await attempt(true)
      } catch (retryError) {
        // 두 번째 실패 — retry 없음
        throw retryError instanceof AdminApiError ? retryError : firstError
      }
    }

    // missing_token, 403, other 4xx/5xx, and unclassified 401: no retry.
    throw firstError
  }

  return {
    get: (path, options) => request('GET', path, options),
    post: (path, options) => request('POST', path, options),
  }
}

let currentUserGetter = () => adminAuth?.currentUser ?? null

// Test seam only: production keeps the Firebase Auth getter above. It avoids
// initializing Firebase browser persistence in isolated transport tests.
export function setAdminApiClientUserGetterForTesting(getter) {
  currentUserGetter = getter || (() => adminAuth?.currentUser ?? null)
  invalidateAdminApiClientRefresh()
}

// The default transport reads Firebase Auth immediately before every request.
// UI code never owns a token or an auth callback dependency.
export const adminApiClient = createAdminApiClient(() => currentUserGetter())
